/**
 * LosaDesignPage — one-way ribbed slab design (E.060 + ACI 318).
 *
 * Sidebar (left): geometry + materials + loads + reinforcement override.
 * Center: cross-section drawing showing two ribs with hollow blocks, top
 *         slab and longitudinal bars.
 * Sidebar (right): outputs + flexion at + and − sections + shear + verifications.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSlabStore } from '@/features/losas/store';
import { useSlabAnalysis } from '@/features/losas/useSlabAnalysis';
import { useExportSlab } from '@/features/losas/useExport';
import { Slab3DView } from '@/features/losas/Slab3DView';
import { RhoBar } from '@/components/ui/RhoBar';
import { ExportCostHint } from '@/features/billing/ExportCostHint';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { Continuity, FlexionRibSection, SlabAnalyzeRequest, SlabType } from '@/features/losas/types';

const SLAB_TYPES: { id: SlabType; label: string; description: string }[] = [
  {
    id: 'aligerada_1d',
    label: 'Aligerada 1D',
    description: 'Viguetas en T con ladrillos. La carga va en UNA dirección. Sistema típico peruano.',
  },
  {
    id: 'maciza_1d',
    label: 'Maciza 1D',
    description: 'Losa sólida de espesor constante. Diseño por franja de 1m que va en UNA dirección.',
  },
  {
    id: 'maciza_2d',
    label: 'Maciza 2D',
    description: 'Losa sólida apoyada en 4 lados. Trabaja en DOS direcciones (Marcus simplificado).',
  },
  {
    id: 'aligerada_2d',
    label: 'Aligerada 2D',
    description: 'Viguetas en T en dos direcciones (cruzadas). Menos común, usa Marcus para repartir carga.',
  },
];

const CONTINUITY_OPTIONS: { value: Continuity; label: string; coef_pos: string; coef_neg: string }[] = [
  { value: 'simply_supported', label: 'Apoyo simple', coef_pos: 'L²/8', coef_neg: '—' },
  { value: 'one_end_continuous', label: 'Un extremo continuo', coef_pos: 'L²/14', coef_neg: 'L²/9' },
  { value: 'both_ends_continuous', label: 'Ambos extremos continuos', coef_pos: 'L²/16', coef_neg: 'L²/10' },
  { value: 'cantilever', label: 'Voladizo', coef_pos: '—', coef_neg: 'L²/2' },
];

export function LosaDesignPage() {
  const request = useSlabStore((s) => s.request);
  const setType = useSlabStore((s) => s.setType);
  const setSection = useSlabStore((s) => s.setSection);
  const setMaterials = useSlabStore((s) => s.setMaterials);
  const setLoads = useSlabStore((s) => s.setLoads);
  const loadRequest = useSlabStore((s) => s.loadRequest);

  // URL hydration: /app/slabs/{id} loads a saved design into the store.
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as SlabAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);
  const [saveOpen, setSaveOpen] = useState(false);

  const { data, isFetching, isError, error } = useSlabAnalysis(request);
  const exporter = useExportSlab();
  const cont = CONTINUITY_OPTIONS.find((c) => c.value === request.section.continuity)!;
  const slabType = request.section.type;
  const isTwoWay = slabType === 'aligerada_2d' || slabType === 'maciza_2d';
  const isSolid = slabType === 'maciza_1d' || slabType === 'maciza_2d';
  const typeInfo = SLAB_TYPES.find((t) => t.id === slabType)!;
  // 2D / 3D toggle for the secondary diagram (elevation or plan view).
  // When set to '3D' the secondary card is replaced by the full 3D viewer.
  const [view3D, setView3D] = useState<'2D' | '3D'>('2D');

  // Hidden 3D viewer for PDF snapshot capture (always mounted off-screen so
  // toDataURL returns a valid PNG even when the user is in 2D mode).
  const snapshotContainerRef = useRef<HTMLDivElement | null>(null);
  const captureSnapshot = (): string | null => {
    const el = snapshotContainerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!el) return null;
    try {
      return el.toDataURL('image/png');
    } catch (e) {
      console.warn('[slab-3d] toDataURL failed:', e);
      return null;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Software E.060
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Diseño de losa · {typeInfo.label}
          </div>
        </div>
        {/* Right side intentionally empty — DesignToolbar below handles status + exports. */}
      </header>

      {/* Per-slab-type independence — switching the type while on a
          /app/slabs/:id route falls back to "Guardar" semantics. */}
      {(() => {
        const matchingDesign =
          designQuery.data && designQuery.data.subtype === slabType ? designQuery.data : null;
        return (
          <>
            <DesignToolbar
              status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
              statusLabel={isError ? `Error: ${String(error)}` : undefined}
              onExportPdf={() => exporter.trigger('pdf', request, captureSnapshot())}
              onExportXlsx={() => exporter.trigger('xlsx', request, captureSnapshot())}
              busyExport={exporter.busy}
              onSave={() => setSaveOpen(true)}
              isUpdate={!!matchingDesign}
            />
            <SaveDesignDialog
              open={saveOpen}
              onOpenChange={setSaveOpen}
              module="losas"
              subtype={slabType}
              payload={request as unknown as Record<string, unknown>}
              existing={matchingDesign}
            />
          </>
        );
      })()}

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — Inputs */}
        <aside
          className="flex w-[280px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <Group title="Tipo de losa">
            <div className="grid grid-cols-2 gap-1">
              {SLAB_TYPES.map((t) => {
                const active = t.id === slabType;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setType(t.id)}
                    className="rounded-[var(--radius-sm)] px-1.5 py-1.5 text-[10px] font-medium transition-colors"
                    style={{
                      background: active ? 'var(--accent)' : 'var(--surface-2)',
                      color: active ? 'var(--surface-base)' : 'var(--text-secondary)',
                      border: '1px solid',
                      borderColor: active ? 'var(--accent)' : 'var(--border-default)',
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div
              className="mt-2 rounded-[var(--radius-sm)] border px-2 py-1.5 text-[10px]"
              style={{
                background: 'rgba(245,158,11,0.06)',
                borderColor: 'rgba(245,158,11,0.30)',
                color: 'var(--text-secondary)',
                lineHeight: 1.45,
              }}
            >
              <div style={{ color: 'var(--accent)', fontWeight: 700 }}>{typeInfo.label}</div>
              <div className="mt-1" style={{ color: 'var(--text-muted)' }}>{typeInfo.description}</div>
            </div>
          </Group>

          <Group title="Materiales">
            <NumberRow label="f'c (kgf/cm²)" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} />
            <NumberRow label="fy (kgf/cm²)" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} />
            <NumberRow label="Recubrimiento (cm)" value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
            <NumberRow label="TMA (cm)" value={request.materials.TMA} onChange={(v) => setMaterials({ TMA: v })} step={0.5} />
          </Group>

          <Group title="Geometría">
            <NumberRow
              label={isTwoWay ? 'La (cm) — luz corta' : 'L (cm) — luz libre'}
              value={request.section.L_span}
              onChange={(v) => setSection({ L_span: v })}
              step={25}
            />
            {isTwoWay && (
              <NumberRow
                label="Lb (cm) — luz larga"
                value={request.section.Lb}
                onChange={(v) => setSection({ Lb: v })}
                step={25}
              />
            )}
            <NumberRow label="h (cm) — espesor total" value={request.section.h_total} onChange={(v) => setSection({ h_total: v })} step={5} />
            {!isSolid && (
              <>
                <NumberRow label="bw (cm) — ancho vigueta" value={request.section.bw} onChange={(v) => setSection({ bw: v })} step={1} />
                <NumberRow label="s (cm) — espaciam." value={request.section.s} onChange={(v) => setSection({ s: v })} step={5} />
                <NumberRow label="t (cm) — losa superior" value={request.section.t_slab} onChange={(v) => setSection({ t_slab: v })} step={1} />
              </>
            )}
            {isSolid && (
              <div className="text-[9.5px] italic" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
                Maciza: diseño por franja de 1 m. No requiere bw, s, t_slab.
              </div>
            )}
            <SelectRow
              label={isTwoWay ? 'Continuidad corta' : 'Continuidad'}
              value={request.section.continuity}
              options={CONTINUITY_OPTIONS.map((c) => ({ value: c.value, label: c.label }))}
              onChange={(v) => setSection({ continuity: v as Continuity })}
            />
            {isTwoWay && (
              <SelectRow
                label="Continuidad larga"
                value={request.section.continuity_long}
                options={CONTINUITY_OPTIONS.map((c) => ({ value: c.value, label: c.label }))}
                onChange={(v) => setSection({ continuity_long: v as Continuity })}
              />
            )}
            <div className="mt-1 text-[9.5px]" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
              +M = Wu·{cont.coef_pos}, −M = Wu·{cont.coef_neg}
            </div>
          </Group>

          <Group title="Cargas (kgf/m²)">
            <NumberRow label="WD (muerta)" value={request.loads.WD} onChange={(v) => setLoads({ WD: v })} step={50} />
            <NumberRow label="WL (viva)" value={request.loads.WL} onChange={(v) => setLoads({ WL: v })} step={50} />
            <div
              className="mt-1 rounded-[var(--radius-sm)] border px-2 py-1.5 text-[10px]"
              style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
            >
              Wu = 1.4·{request.loads.WD} + 1.7·{request.loads.WL} = <strong style={{ color: 'var(--accent)' }}>{(1.4 * request.loads.WD + 1.7 * request.loads.WL).toFixed(0)}</strong> kgf/m²
            </div>
          </Group>
        </aside>

        {/* CENTER — Cross-section drawing */}
        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          {data && (
            <div className="flex flex-col gap-4">
              <DiagramCard
                title={
                  isSolid
                    ? 'Sección transversal — losa maciza (franja de 1 m)'
                    : 'Sección transversal — losa aligerada (2 viguetas + 1 ladrillo)'
                }
              >
                <SlabSectionDiagram
                  h={request.section.h_total}
                  bw={request.section.bw}
                  s={request.section.s}
                  t_slab={request.section.t_slab}
                  cover={request.materials.cover}
                  d_pos={data.flexion_pos.bars.diameters[0] ?? '1/2'}
                  d_neg={data.flexion_neg.bars.diameters[0] ?? '1/2'}
                  n_pos={data.flexion_pos.bars.n_bars}
                  n_neg={data.flexion_neg.bars.n_bars}
                  solid={isSolid}
                />
              </DiagramCard>
              <DiagramCard
                title={view3D === '3D' ? 'Vista 3D — modelo del paño completo' : 'Vista en planta'}
                toolbar={
                  <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                    {(['2D', '3D'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setView3D(m)}
                        className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                        style={{
                          background: view3D === m ? 'var(--accent)' : 'transparent',
                          color: view3D === m ? 'var(--surface-base)' : 'var(--text-secondary)',
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                }
              >
                {view3D === '3D' ? (
                  <div style={{ width: 880, height: 460 }}>
                    <Slab3DView
                      slabType={slabType}
                      L={request.section.L_span}
                      Lb={request.section.Lb}
                      h={request.section.h_total}
                      t_slab={request.section.t_slab}
                      bw={request.section.bw}
                      s={request.section.s}
                      cover={request.materials.cover}
                      n_pos={data.flexion_pos.bars.n_bars}
                      n_neg={data.flexion_neg.bars.n_bars}
                      d_bar_pos={diamInchToCm(data.flexion_pos.bars.diameters[0] ?? '1/2')}
                      d_bar_neg={diamInchToCm(data.flexion_neg.bars.diameters[0] ?? '1/2')}
                      n_pos_long={data.flexion_pos_long?.bars.n_bars ?? 0}
                      d_bar_pos_long={diamInchToCm(
                        data.flexion_pos_long?.bars.diameters[0] ?? '1/2',
                      )}
                      continuity={request.section.continuity}
                      continuity_long={request.section.continuity_long}
                    />
                  </div>
                ) : data.is_two_way ? (
                  <SlabPlanDiagram
                    La={request.section.L_span}
                    Lb={request.section.Lb}
                    Wa_pct={data.Wa_fraction * 100}
                    Wb_pct={data.Wb_fraction * 100}
                    posShort={data.flexion_pos.bars.label}
                    posLong={data.flexion_pos_long?.bars.label ?? '—'}
                  />
                ) : (
                  <SlabPlan1DDiagram
                    L={request.section.L_span}
                    s={request.section.s}
                    bw={request.section.bw}
                    cont={request.section.continuity}
                    isSolid={isSolid}
                    posLabel={data.flexion_pos.bars.label}
                  />
                )}
              </DiagramCard>

              {view3D !== '3D' && (
                <DiagramCard
                  title={
                    data.is_two_way
                      ? 'Elevación — corte por La (luz corta)'
                      : isSolid
                        ? 'Elevación — franja de 1 m'
                        : 'Elevación de una vigueta (corte longitudinal)'
                  }
                >
                  <RibElevationDiagram
                    L={request.section.L_span}
                    h={request.section.h_total}
                    cont={request.section.continuity}
                    Mu_pos={data.outputs.Mu_pos}
                    Mu_neg={data.outputs.Mu_neg}
                    posLabel={data.flexion_pos.bars.label}
                    negLabel={data.flexion_neg.bars.label}
                  />
                </DiagramCard>
              )}
            </div>
          )}
        </main>

        {/* RIGHT — Results */}
        <aside
          className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {data && (
            <>
              <ResultCard title="Resumen de diseño">
                <KV label="Wu por vigueta" value={`${data.outputs.Wu_per_rib.toFixed(1)} kgf/m`} />
                <KV label="Mu+ (centro)" value={`${(data.outputs.Mu_pos / 1e5).toFixed(3)} tonf·m`} />
                <KV label="Mu− (apoyo)" value={`${(data.outputs.Mu_neg / 1e5).toFixed(3)} tonf·m`} />
                <KV label="Vu por vigueta" value={`${(data.outputs.Vu_per_rib / 1000).toFixed(2)} tonf`} />
                <KV label="d efectivo" value={`${data.outputs.d_effective.toFixed(1)} cm`} />
                <KV label="h_min recomend." value={`${data.outputs.h_min_recommended.toFixed(1)} cm`} />
                <KV label="Acero superior" value={data.outputs.bars_tag_top} mono />
                <KV label="Acero inferior" value={data.outputs.bars_tag_bot} mono />
              </ResultCard>

              {data.is_two_way && (
                <ResultCard title="Reparto de cargas (Marcus)">
                  <KV label="m = La / Lb" value={data.aspect_ratio.toFixed(3)} />
                  <KV label="Wa fracción (corta)" value={`${(data.Wa_fraction * 100).toFixed(1)} %`} />
                  <KV label="Wb fracción (larga)" value={`${(data.Wb_fraction * 100).toFixed(1)} %`} />
                  <div className="mt-1 text-[9.5px]" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
                    La dirección corta es más rígida y absorbe la mayor parte de la carga.
                  </div>
                </ResultCard>
              )}

              <FlexionCard f={data.flexion_pos} />
              <FlexionCard f={data.flexion_neg} />
              {data.flexion_pos_long && <FlexionCard f={data.flexion_pos_long} />}
              {data.flexion_neg_long && <FlexionCard f={data.flexion_neg_long} />}

              {data.temperature_steel.required && (
                <ResultCard title="Acero por contracción y temperatura (E.060 9.7)">
                  <KV label="As_min" value={`${data.temperature_steel.As_min.toFixed(2)} cm²/m`} />
                  <KV label="Diámetro" value={`Ø${data.temperature_steel.bar_diam}"`} />
                  <KV label="Espaciamiento" value={`@ ${data.temperature_steel.spacing_chosen.toFixed(0)} cm`} />
                  <KV label="s máx (norma)" value={`${data.temperature_steel.spacing_max.toFixed(0)} cm`} />
                  <div className="mt-1 rounded-[var(--radius-sm)] px-2 py-1 text-[11px] text-center font-mono"
                    style={{ background: 'rgba(59,130,246,0.12)', color: 'var(--brand-alt)', fontWeight: 700 }}>
                    {data.temperature_steel.label}
                  </div>
                  <div className="mt-1 text-[9.5px]" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
                    Va PERPENDICULAR al armado principal, en la cara superior de la losa maciza.
                  </div>
                </ResultCard>
              )}

              <ResultCard title="Deflexión (estado de servicio)">
                <KV label="Δ inmediata (Δi)" value={`${data.deflection.delta_instant.toFixed(3)} cm`} />
                <KV label="Δ diferida (Δlt)" value={`${data.deflection.delta_long_term.toFixed(3)} cm`} />
                <KV label="Límite L/240 (piso)" value={`${data.deflection.limit_floor.toFixed(2)} cm`} />
                <KV label="Límite L/480 (tabiques)" value={`${data.deflection.limit_partition.toFixed(2)} cm`} />
                <div className="mt-1 flex flex-col gap-0.5 text-[10px]">
                  <div style={{ color: data.deflection.ok_floor ? 'var(--success)' : 'var(--danger)' }}>
                    {data.deflection.ok_floor ? '✓' : '✗'} Δlt ≤ L/240 (piso sin tabiques)
                  </div>
                  <div style={{ color: data.deflection.ok_partition ? 'var(--success)' : 'var(--danger)' }}>
                    {data.deflection.ok_partition ? '✓' : '✗'} Δlt ≤ L/480 (con tabiques susceptibles)
                  </div>
                </div>
                <div className="mt-1 text-[9.5px]" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
                  Δlt = Δi·(1+λΔ) con λΔ=2 (5 años, sin acero compresión). Análisis con Ig.
                </div>
              </ResultCard>

              <ResultCard title="Cortante (Vc sin estribos)">
                <KV label="Vu por vigueta" value={`${data.shear.Vu_per_rib.toFixed(0)} kgf`} />
                <KV label="Vc" value={`${data.shear.Vc.toFixed(0)} kgf`} />
                <KV label="φVc" value={`${data.shear.phi_Vc.toFixed(0)} kgf`} />
                <div className="mt-1 text-[10px]" style={{ color: data.shear.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.shear.ok
                    ? '✓ Vu < φVc — sin estribos'
                    : '✗ Vu > φVc — REQUIERE ENSANCHE de la vigueta cerca de los apoyos'}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-0.5">
                    <span
                      className="mt-0.5"
                      style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, minWidth: 12 }}
                    >
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]" style={{ lineHeight: 1.35 }}>
                      {v.label}
                    </span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>

      {/* HIDDEN 3D viewer — always mounted off-screen so that captureSnapshot()
          can call toDataURL() to embed the render into the exported PDF. */}
      {data && (
        <div
          ref={snapshotContainerRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: -2400,
            width: 1100,
            height: 600,
            pointerEvents: 'none',
            opacity: 0,
          }}
        >
          <Slab3DView
            slabType={slabType}
            L={request.section.L_span}
            Lb={request.section.Lb}
            h={request.section.h_total}
            t_slab={request.section.t_slab}
            bw={request.section.bw}
            s={request.section.s}
            cover={request.materials.cover}
            n_pos={data.flexion_pos.bars.n_bars}
            n_neg={data.flexion_neg.bars.n_bars}
            d_bar_pos={diamInchToCm(data.flexion_pos.bars.diameters[0] ?? '1/2')}
            d_bar_neg={diamInchToCm(data.flexion_neg.bars.diameters[0] ?? '1/2')}
            n_pos_long={data.flexion_pos_long?.bars.n_bars ?? 0}
            d_bar_pos_long={diamInchToCm(data.flexion_pos_long?.bars.diameters[0] ?? '1/2')}
            continuity={request.section.continuity}
            continuity_long={request.section.continuity_long}
          />
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */
/*  Inputs                                                                  */
/* ====================================================================== */

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function NumberRow({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
        className="w-[110px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-[150px] rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/* ====================================================================== */
/*  Results cards                                                           */
/* ====================================================================== */

function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2"
      style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px]' : 'text-[10.5px]'} style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

function FlexionCard({ f }: { f: FlexionRibSection }) {
  return (
    <ResultCard title={`Flexión — ${f.label}${f.as_T_section ? ' · T-beam' : ' · rect bw'}`}>
      <KV label="Mu" value={`${(f.Mu / 1e5).toFixed(3)} tonf·m`} />
      <KV label="φMn" value={`${(f.phi_Mn / 1e5).toFixed(3)} tonf·m`} />
      <KV label="As requerido" value={`${f.bars.As_required.toFixed(2)} cm²`} />
      <KV label="As provisto" value={`${f.bars.As_provided.toFixed(2)} cm²`} />
      <KV label="Barras" value={f.bars.label} mono />
      <div className="mt-2">
        <RhoBar
          rho={f.bars.rho}
          rho_min={f.bars.rho_min}
          rho_max={f.bars.rho_max}
          hideAdvice
        />
      </div>
      <div className="mt-1 text-[9.5px]" style={{ color: f.ok ? 'var(--success)' : 'var(--danger)' }}>
        {f.ok ? '✓ ' : '✗ '}
        {f.governing}
      </div>
    </ResultCard>
  );
}

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525,
  '1/2': 1.27,
  '5/8': 1.5875,
  '3/4': 1.905,
  '1': 2.54,
  '1 3/8': 3.4925,
};

function diamInchToCm(d: string): number {
  return DIAM_INCH_TO_CM[d] ?? 1.27;
}

function DiagramCard({
  title,
  children,
  toolbar,
}: {
  title: string;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border p-3"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* ====================================================================== */
/*  SLAB CROSS-SECTION DIAGRAM                                              */
/* ====================================================================== */

function SlabSectionDiagram({
  h,
  bw,
  s,
  t_slab,
  cover,
  n_pos,
  n_neg,
  solid = false,
}: {
  h: number;
  bw: number;
  s: number;
  t_slab: number;
  cover: number;
  d_pos: string;
  d_neg: string;
  n_pos: number;
  n_neg: number;
  /** When true the slab is drawn solid (maciza) — no ribs, no ladrillos. */
  solid?: boolean;
}) {
  // ---- SOLID SLAB MODE ------------------------------------------------
  // Draw a 1-m wide strip (no ribs, no hollow blocks). Bars distributed
  // across the strip width at top and bottom faces.
  if (solid) {
    return <SolidSlabSection h={h} cover={cover} n_pos={n_pos} n_neg={n_neg} />;
  }

  // Drawing: show two ribs with one hollow block between them.
  // Total width displayed = 2·s (two rib centers, one block in between).
  const totalCm = 2 * s + bw;
  const W = 760;
  const H = 280;
  const PAD = 40;
  const scale = Math.min((W - 2 * PAD) / totalCm, (H - 2 * PAD) / (h + 20));

  // y axis: 0 at top, +y down (svg). Place origin at the BOTTOM of the slab.
  const yTop = PAD;
  const yBot = yTop + h * scale;
  const xL = (W - totalCm * scale) / 2;

  // Rib positions (left edge of each rib in slab coords)
  const ribLeftX = xL;
  const ribRightX = xL + (s + bw) * scale; // second rib starts at s + bw

  // Block (ladrillo) between ribs
  const blockX1 = ribLeftX + bw * scale;
  const blockX2 = ribRightX;
  const blockY1 = yTop + t_slab * scale;
  const blockY2 = yBot;
  // Half-block on the right side (continuation)
  const halfBlockX1 = ribRightX + bw * scale;
  const halfBlockX2 = xL + totalCm * scale;

  // Bar positions: y_top = inside top of slab, y_bot = inside bottom of rib
  const yBarsTop = yTop + (cover + 0.5) * scale;
  const yBarsBot = yBot - (cover + 0.5) * scale;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <pattern id="hatch-slab" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
        <pattern id="brick-slab" patternUnits="userSpaceOnUse" width="16" height="10">
          <rect width="16" height="10" fill="#A16207" opacity="0.55" />
          <line x1="0" y1="0" x2="16" y2="0" stroke="#451A03" strokeWidth="0.6" />
          <line x1="0" y1="5" x2="16" y2="5" stroke="#451A03" strokeWidth="0.6" />
          <line x1="8" y1="0" x2="8" y2="5" stroke="#451A03" strokeWidth="0.6" />
          <line x1="0" y1="5" x2="0" y2="10" stroke="#451A03" strokeWidth="0.6" />
          <line x1="16" y1="5" x2="16" y2="10" stroke="#451A03" strokeWidth="0.6" />
        </pattern>
      </defs>

      {/* Top slab — full width */}
      <rect
        x={xL}
        y={yTop}
        width={totalCm * scale}
        height={t_slab * scale}
        fill="url(#hatch-slab)"
        stroke="var(--concrete-outline)"
        strokeWidth="1.2"
      />

      {/* Hollow block (ladrillo) — between the two visible ribs */}
      <rect
        x={blockX1}
        y={blockY1}
        width={blockX2 - blockX1}
        height={blockY2 - blockY1}
        fill="url(#brick-slab)"
        stroke="#451A03"
        strokeWidth="0.6"
      />
      {/* Half block on the right (suggests the pattern continues) */}
      <rect
        x={halfBlockX1}
        y={blockY1}
        width={halfBlockX2 - halfBlockX1}
        height={blockY2 - blockY1}
        fill="url(#brick-slab)"
        stroke="#451A03"
        strokeWidth="0.6"
      />

      {/* Rib 1 (left) — concrete hatched, full height */}
      <rect
        x={ribLeftX}
        y={yTop}
        width={bw * scale}
        height={h * scale}
        fill="url(#hatch-slab)"
        stroke="var(--concrete-outline)"
        strokeWidth="1.2"
      />
      {/* Rib 2 (middle/right) */}
      <rect
        x={ribRightX}
        y={yTop}
        width={bw * scale}
        height={h * scale}
        fill="url(#hatch-slab)"
        stroke="var(--concrete-outline)"
        strokeWidth="1.2"
      />

      {/* Bars — top (negative moment), bottom (positive moment) on each visible rib */}
      {[ribLeftX, ribRightX].map((rx, i) => {
        const cx_rib = rx + (bw * scale) / 2;
        return (
          <g key={`rib-bars-${i}`}>
            {/* Top bars (n_neg evenly across bw) */}
            {Array.from({ length: n_neg }).map((_, k) => {
              const tx = n_neg === 1
                ? cx_rib
                : rx + (cover + 0.5) * scale + (k * (bw - 2 * (cover + 0.5))) / (n_neg - 1) * scale;
              return (
                <circle key={`t-${k}`} cx={tx} cy={yBarsTop} r={3.5} fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
              );
            })}
            {/* Bottom bars (n_pos evenly across bw) */}
            {Array.from({ length: n_pos }).map((_, k) => {
              const bx = n_pos === 1
                ? cx_rib
                : rx + (cover + 0.5) * scale + (k * (bw - 2 * (cover + 0.5))) / (n_pos - 1) * scale;
              return (
                <circle key={`b-${k}`} cx={bx} cy={yBarsBot} r={3.5} fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
              );
            })}
          </g>
        );
      })}

      {/* Dimensions */}
      <text x={W / 2} y={yBot + 26} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        h = {h} cm  ·  bw = {bw} cm  ·  s = {s} cm  ·  t = {t_slab} cm
      </text>
      <text x={ribLeftX + bw * scale / 2} y={yTop - 6} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        vigueta
      </text>
      <text x={(blockX1 + blockX2) / 2} y={yTop - 6} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        ladrillo
      </text>
      <text x={ribRightX + bw * scale / 2} y={yTop - 6} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        vigueta
      </text>

      {/* Legend */}
      <g transform={`translate(8, ${H - 8})`}>
        <circle cx="4" cy="0" r="3.5" fill="var(--accent)" />
        <text x="12" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="monospace">acero</text>
        <rect x="60" y="-4" width="10" height="6" fill="url(#hatch-slab)" stroke="var(--concrete-outline)" strokeWidth="0.6" />
        <text x="74" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="monospace">concreto</text>
        <rect x="140" y="-4" width="10" height="6" fill="url(#brick-slab)" stroke="#451A03" strokeWidth="0.6" />
        <text x="154" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="monospace">ladrillo de techo</text>
      </g>
    </svg>
  );
}

/* ====================================================================== */
/*  RIB ELEVATION DIAGRAM                                                   */
/* ====================================================================== */

function RibElevationDiagram({
  L,
  h,
  cont,
  Mu_pos,
  Mu_neg,
  posLabel,
  negLabel,
}: {
  L: number;
  h: number;
  cont: Continuity;
  Mu_pos: number;
  Mu_neg: number;
  posLabel: string;
  negLabel: string;
}) {
  const W = 880;
  const H = 240;
  const PAD_X = 60;
  const PAD_Y = 50;
  const beamH = Math.min(80, H - 2 * PAD_Y);
  const beamW = W - 2 * PAD_X;
  const xL = PAD_X;
  const xR = xL + beamW;
  const yT = PAD_Y + 20;
  const yB = yT + beamH;

  const showLeftCont = cont === 'one_end_continuous' || cont === 'both_ends_continuous';
  const showRightCont = cont === 'both_ends_continuous';
  const isCantilever = cont === 'cantilever';

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <pattern id="hatch-elev-slab" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.18" strokeWidth="1" />
        </pattern>
      </defs>

      {/* The rib body */}
      <rect x={xL} y={yT} width={beamW} height={beamH}
        fill="url(#hatch-elev-slab)" stroke="var(--concrete-outline)" strokeWidth="1.2" />

      {/* Supports */}
      {!isCantilever && (
        <>
          <polygon points={`${xL},${yB + 1} ${xL - 9},${yB + 18} ${xL + 9},${yB + 18}`} fill="var(--text-muted)" opacity="0.75" />
          <polygon points={`${xR},${yB + 1} ${xR - 9},${yB + 18} ${xR + 9},${yB + 18}`} fill="var(--text-muted)" opacity="0.75" />
        </>
      )}
      {isCantilever && (
        <rect x={xL - 6} y={yT - 4} width="10" height={beamH + 8} fill="var(--text-muted)" opacity="0.55" />
      )}

      {/* Bottom bar — full length */}
      <line x1={xL + 4} y1={yB - 8} x2={xR - 4} y2={yB - 8} stroke="var(--accent)" strokeWidth="2.4" />
      {/* Top bar — only over continuous supports (extends ~L/4 from each cont. end) */}
      {showLeftCont && (
        <line x1={xL + 4} y1={yT + 8} x2={xL + beamW * 0.30} y2={yT + 8} stroke="var(--accent)" strokeWidth="2.4" />
      )}
      {showRightCont && (
        <line x1={xR - beamW * 0.30} y1={yT + 8} x2={xR - 4} y2={yT + 8} stroke="var(--accent)" strokeWidth="2.4" />
      )}
      {isCantilever && (
        <line x1={xL + 4} y1={yT + 8} x2={xR - 4} y2={yT + 8} stroke="var(--accent)" strokeWidth="2.4" />
      )}

      {/* Moment markers — small badges showing Mu+ and Mu− values */}
      {Mu_pos > 0 && (
        <g>
          <text x={(xL + xR) / 2} y={yB + 32} textAnchor="middle" fontSize="10" fontFamily="monospace"
            fill="var(--accent)" style={{ fontWeight: 700 }}>
            +Mu = {(Mu_pos / 1e5).toFixed(3)} tonf·m
          </text>
          <text x={(xL + xR) / 2} y={yB + 46} textAnchor="middle" fontSize="9" fontFamily="monospace"
            fill="var(--text-muted)">
            inferior: {posLabel}
          </text>
        </g>
      )}
      {(showLeftCont || showRightCont || isCantilever) && Mu_neg > 0 && (
        <g>
          <text x={showLeftCont && !showRightCont ? xL + 30 : (xL + xR) / 2}
            y={yT - 18}
            textAnchor={showLeftCont && !showRightCont ? 'start' : 'middle'}
            fontSize="10" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
            −Mu = {(Mu_neg / 1e5).toFixed(3)} tonf·m
          </text>
          <text x={showLeftCont && !showRightCont ? xL + 30 : (xL + xR) / 2}
            y={yT - 6}
            textAnchor={showLeftCont && !showRightCont ? 'start' : 'middle'}
            fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
            superior: {negLabel}
          </text>
        </g>
      )}

      {/* Total length */}
      <text x={(xL + xR) / 2} y={H - 12} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        L = {L} cm  ·  h = {h} cm
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  SOLID SLAB CROSS-SECTION (1-m strip, no ribs no blocks)                 */
/* ====================================================================== */

function SolidSlabSection({
  h,
  cover,
  n_pos,
  n_neg,
}: {
  h: number;
  cover: number;
  n_pos: number;
  n_neg: number;
}) {
  const stripW_cm = 100;
  const W = 760;
  const H = 240;
  const PAD = 40;
  const scale = Math.min((W - 2 * PAD) / stripW_cm, (H - 2 * PAD) / (h * 4));
  const yTop = PAD + 20;
  const yBot = yTop + h * scale;
  const xL = (W - stripW_cm * scale) / 2;
  const xR = xL + stripW_cm * scale;
  const yBarsTop = yTop + (cover + 0.5) * scale;
  const yBarsBot = yBot - (cover + 0.5) * scale;

  const xs = (n: number) =>
    n <= 0 ? [] : n === 1
      ? [(xL + xR) / 2]
      : Array.from({ length: n }, (_, i) =>
          xL + (cover + 1) * scale + (i * (stripW_cm - 2 * (cover + 1)) * scale) / (n - 1),
        );

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <pattern id="hatch-solid" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
      </defs>
      <rect
        x={xL} y={yTop} width={(xR - xL)} height={h * scale}
        fill="url(#hatch-solid)" stroke="var(--concrete-outline)" strokeWidth="1.4"
      />
      {xs(n_neg).map((x, i) => (
        <circle key={`t-${i}`} cx={x} cy={yBarsTop} r={4} fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
      ))}
      {xs(n_pos).map((x, i) => (
        <circle key={`b-${i}`} cx={x} cy={yBarsBot} r={4} fill="var(--accent)" stroke="var(--concrete-outline)" strokeWidth="0.5" />
      ))}
      <text x={(xL + xR) / 2} y={yBot + 26} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        b = 1 m  ·  h = {h} cm  (franja de diseño)
      </text>
      <text x={(xL + xR) / 2} y={yTop - 8} textAnchor="middle" fontSize="10" fontFamily="monospace" fill="var(--text-muted)">
        losa maciza — sección sólida
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  PLAN VIEW for two-way slabs (La x Lb panel)                             */
/* ====================================================================== */

function SlabPlanDiagram({
  La,
  Lb,
  Wa_pct,
  Wb_pct,
  posShort,
  posLong,
}: {
  La: number;
  Lb: number;
  Wa_pct: number;
  Wb_pct: number;
  posShort: string;
  posLong: string;
}) {
  const W = 760;
  const H = 360;
  const PAD = 40;
  const maxL = Math.max(La, Lb, 1);
  const scale = Math.min((W - 2 * PAD) / maxL, (H - 2 * PAD - 60) / maxL);
  const Wpx = Lb * scale;   // panel drawn with Lb horizontal
  const Hpx = La * scale;
  const xL = (W - Wpx) / 2;
  const yT = PAD + 20;
  const xR = xL + Wpx;
  const yB = yT + Hpx;

  // Bars in SHORT direction (going vertically — along La)
  const nShort = 6;
  // Bars in LONG direction (going horizontally — along Lb)
  const nLong = 6;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <pattern id="hatch-plan" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="var(--text-muted)" strokeOpacity="0.12" strokeWidth="1" />
        </pattern>
      </defs>
      {/* Panel concrete */}
      <rect x={xL} y={yT} width={Wpx} height={Hpx}
        fill="url(#hatch-plan)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* Supports — thick lines at the 4 edges suggesting beams */}
      <line x1={xL} y1={yT} x2={xR} y2={yT} stroke="var(--text-secondary)" strokeWidth="4" opacity="0.7" />
      <line x1={xL} y1={yB} x2={xR} y2={yB} stroke="var(--text-secondary)" strokeWidth="4" opacity="0.7" />
      <line x1={xL} y1={yT} x2={xL} y2={yB} stroke="var(--text-secondary)" strokeWidth="4" opacity="0.7" />
      <line x1={xR} y1={yT} x2={xR} y2={yB} stroke="var(--text-secondary)" strokeWidth="4" opacity="0.7" />

      {/* Bars in short direction (perpendicular to long side, i.e. VERTICAL lines parallel to La) */}
      {Array.from({ length: nShort }).map((_, i) => {
        const x = xL + ((i + 1) * Wpx) / (nShort + 1);
        return <line key={`s-${i}`} x1={x} y1={yT + 6} x2={x} y2={yB - 6} stroke="var(--accent)" strokeWidth="1.6" />;
      })}
      {/* Bars in long direction (HORIZONTAL lines parallel to Lb) */}
      {Array.from({ length: nLong }).map((_, i) => {
        const y = yT + ((i + 1) * Hpx) / (nLong + 1);
        return <line key={`l-${i}`} x1={xL + 6} y1={y} x2={xR - 6} y2={y} stroke="var(--brand-violet)" strokeWidth="1.4" strokeDasharray="3 2" />;
      })}

      {/* Dimensions */}
      <text x={(xL + xR) / 2} y={yT - 8} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        Lb = {Lb} cm  (luz larga)
      </text>
      <text x={xL - 6} y={(yT + yB) / 2} textAnchor="end" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)"
        transform={`rotate(-90, ${xL - 6}, ${(yT + yB) / 2})`}>
        La = {La} cm  (luz corta)
      </text>

      {/* Legend */}
      <g transform={`translate(${xL}, ${yB + 26})`}>
        <line x1="0" y1="0" x2="20" y2="0" stroke="var(--accent)" strokeWidth="1.8" />
        <text x="26" y="3" fontSize="10" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
          Acero dir. corta (La) — {Wa_pct.toFixed(1)}% de la carga — {posShort}
        </text>
        <line x1="0" y1="16" x2="20" y2="16" stroke="var(--brand-violet)" strokeWidth="1.4" strokeDasharray="3 2" />
        <text x="26" y="20" fontSize="10" fontFamily="monospace" fill="var(--brand-violet)" style={{ fontWeight: 700 }}>
          Acero dir. larga (Lb) — {Wb_pct.toFixed(1)}% de la carga — {posLong}
        </text>
      </g>
    </svg>
  );
}

/* ====================================================================== */
/*  PLAN VIEW for one-way slabs                                             */
/* ====================================================================== */

/**
 * Plan view for a 1D slab (aligerada or maciza). The slab is drawn as a
 * rectangle with the joists running parallel to the L_span direction (for
 * aligerada — shown as evenly spaced lines) or as a flat solid panel (for
 * maciza). The two supporting beams are drawn at the LEFT and RIGHT edges,
 * with hatching style depending on the continuity at each end.
 */
function SlabPlan1DDiagram({
  L,
  s,
  bw,
  cont,
  isSolid,
  posLabel,
}: {
  L: number;
  s: number;
  bw: number;
  cont: Continuity;
  isSolid: boolean;
  posLabel: string;
}) {
  const W = 760;
  // The vertical (transverse to L_span) extent of the slab is arbitrary in
  // a plan view — pick a representative width that shows several joists.
  const W_trans = isSolid ? 200 : Math.max(3 * s, 120);
  const PAD = 40;
  const drawW = W - 2 * PAD;
  const drawH = 220;
  const scaleX = drawW / L;
  const scaleY = Math.min(scaleX, drawH / W_trans);
  // Use the same scale for both axes so the joist spacing reads correctly
  const scale = Math.min(scaleX, scaleY);

  const wpx = L * scale;
  const hpx = W_trans * scale;
  const H = hpx + 90;
  const xL = (W - wpx) / 2;
  const yT = 30;
  const yB = yT + hpx;

  const showLeftCont = cont === 'one_end_continuous' || cont === 'both_ends_continuous';
  const showRightCont = cont === 'both_ends_continuous';
  const isCantilever = cont === 'cantilever';

  // Number of joists visible in the trans direction
  const n_joists = isSolid ? 0 : Math.max(2, Math.floor(W_trans / s));

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-plan1d" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.16" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Slab area */}
      <rect x={xL} y={yT} width={wpx} height={hpx}
        fill="url(#hatch-plan1d)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* Joists (for ribbed) — lines running parallel to L_span across the slab */}
      {!isSolid && Array.from({ length: n_joists }).map((_, i) => {
        const yLine = yT + ((i + 1) * hpx) / (n_joists + 1);
        // Each joist is drawn as a thin filled rectangle (the rib width)
        const ribH = bw * scale;
        return (
          <rect
            key={`joist-${i}`}
            x={xL + 4}
            y={yLine - ribH / 2}
            width={wpx - 8}
            height={Math.max(ribH, 1.5)}
            fill="rgba(245,158,11,0.32)"
            stroke="var(--accent)"
            strokeWidth="0.6"
          />
        );
      })}

      {/* Supports — left edge */}
      {!isCantilever && (
        <rect
          x={xL - 8} y={yT - 6} width="8" height={hpx + 12}
          fill={showLeftCont ? 'var(--text-secondary)' : 'transparent'}
          stroke="var(--text-secondary)"
          strokeWidth="1.6"
          opacity="0.7"
        />
      )}
      {/* Right edge */}
      {!isCantilever && (
        <rect
          x={xL + wpx} y={yT - 6} width="8" height={hpx + 12}
          fill={showRightCont ? 'var(--text-secondary)' : 'transparent'}
          stroke="var(--text-secondary)"
          strokeWidth="1.6"
          opacity="0.7"
        />
      )}
      {/* Cantilever — wall on the left, free end on the right */}
      {isCantilever && (
        <rect x={xL - 10} y={yT - 6} width="10" height={hpx + 12}
          fill="var(--text-secondary)" opacity="0.5" />
      )}

      {/* Direction-of-span arrow */}
      <g transform={`translate(${xL + wpx / 2}, ${yT + hpx / 2})`}>
        <line x1="-40" y1="0" x2="40" y2="0" stroke="var(--accent)" strokeWidth="1.5" />
        <polygon points="40,0 32,-5 32,5" fill="var(--accent)" />
        <polygon points="-40,0 -32,-5 -32,5" fill="var(--accent)" />
        <text x="0" y="-8" textAnchor="middle" fontSize="10" fontFamily="monospace"
          fill="var(--accent)" style={{ fontWeight: 700 }}>
          dirección de armado
        </text>
      </g>

      {/* Dimensions */}
      <text x={W / 2} y={yB + 16} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        L = {L} cm (luz libre)  ·  s = {s} cm  ·  bw = {bw} cm
      </text>
      <text x={W / 2} y={yB + 30} textAnchor="middle" fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)">
        {isSolid
          ? `Losa maciza · acero principal en la dirección mostrada: ${posLabel}`
          : `Aligerada · viguetas Ø${posLabel} cada s = ${s} cm c/c`}
      </text>

      {/* Continuity labels */}
      <text x={xL - 14} y={yT - 8} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--text-muted)" style={{ fontStyle: 'italic' }}>
        {isCantilever ? 'empotrado' : showLeftCont ? 'continuo' : 'apoyo simple'}
      </text>
      <text x={xL + wpx + 14} y={yT - 8} fontSize="9" fontFamily="monospace" fill="var(--text-muted)" style={{ fontStyle: 'italic' }}>
        {isCantilever ? 'extremo libre' : showRightCont ? 'continuo' : 'apoyo simple'}
      </text>
    </svg>
  );
}

